import React from 'react';
import { useNavigate } from 'react-router-dom';
import InlineAlertBanner from './InlineAlertBanner';
import { apiFetch } from '../services/api';

const T = {
  fr: {
    message: (version) => (
      <>Mise à jour du thème <strong>v{version}</strong> disponible.</>
    ),
    action: 'Voir la mise à jour',
  },
  en: {
    message: (version) => (
      <>Theme update <strong>v{version}</strong> available.</>
    ),
    action: 'View update',
  },
  ar: {
    message: (version) => (
      <>تحديث القالب <strong>v{version}</strong> متاح.</>
    ),
    action: 'عرض التحديث',
  },
};

export default function ThemeUpdateNotificationBanner({ storeSlug, lang = 'fr' }) {
  const navigate = useNavigate();
  const [notification, setNotification] = React.useState(null);
  const [navigating, setNavigating] = React.useState(false);

  const t = T[lang] || T.fr;
  const isRtl = lang === 'ar';

  React.useEffect(() => {
    if (!storeSlug) {
      setNotification(null);
      return undefined;
    }

    let cancelled = false;

    apiFetch(`/store/${storeSlug}/all-notifications/?viewed=false&format=json`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const results = Array.isArray(data.results) ? data.results : [];
        const themeUpdates = results
          .filter((item) => item?.auxiliary?.status === 'updated_theme')
          .sort((a, b) => new Date(b.created) - new Date(a.created));
        if (!themeUpdates.length) return;

        const [latest, ...stale] = themeUpdates;
        setNotification({ id: latest.id, version: latest.auxiliary.version });

        // Only the latest version matters — silently clear older duplicate
        // notifications so they don't resurface one-by-one on later visits.
        stale.forEach((item) => {
          apiFetch(`/store/${storeSlug}/notifications/?format=json`, {
            method: 'PUT',
            body: JSON.stringify({ id: item.id }),
          }).catch(() => {});
        });
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [storeSlug]);

  const handleAction = React.useCallback(async () => {
    if (!notification || navigating) return;
    setNavigating(true);
    try {
      await apiFetch(`/store/${storeSlug}/notifications/?format=json`, {
        method: 'PUT',
        body: JSON.stringify({ id: notification.id }),
      });
    } catch {
      // Non-critical — navigate regardless.
    }
    setNotification(null);
    navigate('/channels/kiostore');
  }, [notification, navigating, storeSlug, navigate]);

  if (!notification) return null;

  return (
    <InlineAlertBanner
      dir={isRtl ? 'rtl' : 'ltr'}
      message={t.message(notification.version)}
      actionLabel={t.action}
      onAction={handleAction}
      loading={navigating}
    />
  );
}
